import React, { useState, useEffect } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import './Auth.css';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [appSettings, setAppSettings] = useState({
    hideRegistration: false,
    maintenanceMode: false,
    maintenanceMessage: 'VoiceMeet Pro is under maintenance. Please try again later.',
  });

  const { login, register, googleLogin } = useAuth();
  const navigate = useNavigate();

  const handleGoogleOAuth = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      try {
        await googleLogin(tokenResponse.access_token);
        toast.success('Welcome!');
        navigate('/dashboard');
      } catch (err) {
        toast.error(err.response?.data?.error || 'Google sign-in failed. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    onError: () => toast.error('Google sign-in was cancelled or failed.'),
  });

  // Load app settings from localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem('voicemeet_app_settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        // Validate parsed data has expected structure
        if (typeof parsed === 'object' && parsed !== null) {
          setAppSettings(prev => ({ ...prev, ...parsed }));
        }
      } catch (e) {
        console.error('Failed to parse app settings:', e);
        localStorage.removeItem('voicemeet_app_settings');
      }
    }
  }, []);

  // Input validation
  const validateInputs = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email.trim())) {
      toast.error('Please enter a valid email address');
      return false;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return false;
    }

    if (isRegister) {
      const trimmedName = name.trim();
      if (trimmedName.length < 2) {
        toast.error('Name must be at least 2 characters');
        return false;
      }
      if (trimmedName.length > 100) {
        toast.error('Name must be less than 100 characters');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate inputs before submitting
    if (!validateInputs()) {
      return;
    }

    setLoading(true);

    try {
      if (isRegister) {
        await register(name.trim(), email.trim().toLowerCase(), password);
        toast.success('Account created successfully! Welcome aboard!');
      } else {
        await login(email.trim().toLowerCase(), password);
        toast.success('Welcome back!');
      }
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show maintenance mode message
  if (appSettings.maintenanceMode) {
    return (
      <div className="auth-container">
        <motion.div
          className="auth-box maintenance-box"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="auth-header">
            <div className="brand-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5z"/><path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/></svg>
            </div>
            <h1>VoiceMeet Pro</h1>
            <h2>Under Maintenance</h2>
          </div>
          <div className="maintenance-content">
            <p>{appSettings.maintenanceMessage}</p>
            <p className="maintenance-note">Please check back later.</p>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <motion.div
        className="trial-promo-sticker"
        initial={{ opacity: 0, y: -16, scale: 0.94 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay: 0.1, duration: 0.5, ease: [0, 0, 0.2, 1] }}
      >
        <div className="trial-promo-sticker__shine" />
        <span className="trial-promo-sticker__icon">🚀</span>
        <div className="trial-promo-sticker__body">
          <span className="trial-promo-sticker__headline">Join Free — 14-Day Trial</span>
          <span className="trial-promo-sticker__perks">No credit card &nbsp;·&nbsp; AI Transcription &nbsp;·&nbsp; 20 Participants</span>
        </div>
        <span className="trial-promo-sticker__tag">FREE</span>
      </motion.div>
      <motion.div
        className="auth-box"
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0, 0, 0.2, 1] }}
      >
        <div className="auth-header">
          <motion.div
            className="brand-icon"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 15 }}
          >
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5z"/><path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/></svg>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
          >
            VoiceMeet Pro
          </motion.h1>
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.4 }}
          >
            {isRegister ? 'Create Account' : 'Welcome Back'}
          </motion.h2>
          <motion.p
            className="auth-tagline"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.4 }}
          >
            Where Every Voice Matters
          </motion.p>
        </div>

        <form onSubmit={handleSubmit}>
          <AnimatePresence mode="wait">
            {isRegister && (
              <motion.div
                key="name-field"
                className="form-group"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
              >
                <label>Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="Enter your name"
                  minLength={2}
                  maxLength={100}
                  autoComplete="name"
                />
              </motion.div>
            )}
          </AnimatePresence>

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
              maxLength={255}
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
              minLength={6}
              maxLength={100}
              autoComplete={isRegister ? 'new-password' : 'current-password'}
            />
            {!isRegister && (
              <Link to="/forgot-password" className="forgot-password-link">
                Forgot password?
              </Link>
            )}
          </div>

          <motion.button
            type="submit"
            disabled={loading}
            className="btn-primary"
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
          >
            {loading ? 'Please wait...' : (isRegister ? 'Create Account' : 'Sign In')}
          </motion.button>
        </form>

        <div className="divider"><span>or</span></div>

        <button
          type="button"
          className="btn-google"
          onClick={() => handleGoogleOAuth()}
          disabled={loading}
        >
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          {isRegister ? 'Sign up with Google' : 'Sign in with Google'}
        </button>

        {/* Only show registration toggle if not hidden by admin */}
        {!appSettings.hideRegistration && (
          <p className="toggle-auth">
            {isRegister ? 'Already have an account?' : "Don't have an account?"}
            <button
              onClick={() => setIsRegister(!isRegister)}
              className="link-button"
            >
              {isRegister ? 'Sign In' : 'Create Account'}
            </button>
          </p>
        )}

        {/* Show message if registration is hidden and user is on register mode */}
        {appSettings.hideRegistration && isRegister && (
          <p className="registration-closed">
            Registration is currently closed. Please contact the administrator.
          </p>
        )}
      </motion.div>
    </div>
  );
}

export default Login;
